import React, { useState } from 'react';
import { Plus, X, Calendar, Clock, AlertTriangle, BarChart2, FileText, Link as LinkIcon, File, ChevronDown, ChevronUp, CheckCircle } from 'react-feather';
import { FeatureTag } from '../ui/feature-tag';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { featureTagApi } from '../../services/api';
import FeatureTagDetailModal from './FeatureTagDetailModal';
import './TagBar.css';

const TagBar = ({ 
  tags, 
  selectedTagId, 
  onTagSelect, 
  onTagCreate,
  onTagUpdate,
  onTagDelete,
  projectId,
  untaggedCount = 0,
  className = "",
  tasks = [],
  users = []
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingDueDate, setEditingDueDate] = useState(null);
  const [newDueDate, setNewDueDate] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTagForDetails, setSelectedTagForDetails] = useState(null);
  const [showCompletedSection, setShowCompletedSection] = useState(false);

  const handleTagSelect = (tagId) => {
    onTagSelect(tagId);
  };

  // Helper function to format due date
  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)}d overdue`, status: 'overdue' };
    } else if (diffDays === 0) {
      return { text: 'Due today', status: 'due-today' };
    } else if (diffDays <= 3) {
      return { text: `${diffDays}d left`, status: 'urgent' };
    } else if (diffDays <= 7) {
      return { text: `${diffDays}d left`, status: 'upcoming' };
    } else {
      return { text: date.toLocaleDateString(), status: 'normal' };
    }
  };

  // Helper function to get due date status
  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return 'no-due-date';
    const date = new Date(dueDate);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'due-today';
    if (diffDays <= 3) return 'urgent';
    if (diffDays <= 7) return 'upcoming';
    return 'normal';
  };

  // Handle due date update
  const handleDueDateUpdate = async (tagId, dueDate) => {
    try {
      const updatedTag = await featureTagApi.updateDueDate(tagId, dueDate);
      if (onTagUpdate) {
        onTagUpdate(updatedTag);
      }
    } catch (error) {
      console.error('Error updating due date:', error);
      alert(error.response?.data?.error || 'Failed to update due date');
    }
  };

  // Handle due date edit start
  const handleStartDueDateEdit = (tag) => {
    setEditingDueDate(tag.id);
    setNewDueDate(tag.due_date ? new Date(tag.due_date).toISOString().split('T')[0] : '');
  };

  // Handle due date edit save
  const handleSaveDueDate = async (tagId) => {
    try {
      const dueDate = newDueDate ? new Date(newDueDate).toISOString() : null;
      await handleDueDateUpdate(tagId, dueDate);
      setEditingDueDate(null);
      setNewDueDate('');
    } catch (error) {
      console.error('Error saving due date:', error);
    }
  };

  // Handle due date edit cancel
  const handleCancelDueDateEdit = () => {
    setEditingDueDate(null);
    setNewDueDate('');
  };

  // Handle opening detail modal
  const handleOpenDetailModal = (tag) => {
    setSelectedTagForDetails(tag);
    setShowDetailModal(true);
  };

  // Handle closing detail modal
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTagForDetails(null);
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    try {
      setCreating(true);
      const newTag = await featureTagApi.create({
        name: newTagName.trim(),
        project_id: projectId
      });

      setNewTagName('');
      setShowCreateForm(false);
      
      if (onTagCreate) {
        onTagCreate(newTag);
      }
    } catch (error) {
      console.error('Error creating feature tag:', error);
      alert(error.response?.data?.error || 'Failed to create feature tag');
    } finally {
      setCreating(false);
    }
  };

  const handleQuickCreate = async (tagName) => {
    try {
      const newTag = await featureTagApi.create({
        name: tagName,
        project_id: projectId
      });
      
      if (onTagCreate) {
        onTagCreate(newTag);
      }
      
      // Auto-select the newly created tag
      onTagSelect(newTag.id);
    } catch (error) {
      console.error('Error creating feature tag:', error);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Are you sure you want to delete the "${tag.name}" tag? This will remove it from all tasks and cannot be undone.`)) {
      return;
    }

    try {
      await featureTagApi.delete(tag.id);
      
      if (onTagDelete) {
        onTagDelete(tag.id);
      }
      
      // If the deleted tag was selected, clear the selection
      if (selectedTagId === tag.id) {
        onTagSelect(null);
      }
    } catch (error) {
      console.error('Error deleting feature tag:', error);
      alert(error.response?.data?.error || 'Failed to delete feature tag');
    }
  };

  return (
    <div className={`tag-bar ${className}`}>
      <div className="tag-bar__content">
        {/* All Tasks Filter */}
        <button
          className={`tag-bar__filter ${selectedTagId === null ? 'active' : ''}`}
          onClick={() => handleTagSelect(null)}
        >
          All Tasks
        </button>

        {/* Untagged Filter */}
        <button
          className={`tag-bar__filter ${selectedTagId === 'untagged' ? 'active' : ''}`}
          onClick={() => handleTagSelect('untagged')}
        >
          Untagged
          <span className="tag-bar__count">({untaggedCount})</span>
        </button>

        {/* Existing Tags - Active */}
        {tags
          .filter(tag => {
            // Hide tags with no tasks
            const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
            if (tagTasks.length === 0) return false;
            
            // Separate completed tags
            const completedTasks = tagTasks.filter(task => ['completed', 'released'].includes(task.status));
            const isCompleted = tagTasks.length > 0 && completedTasks.length === tagTasks.length;
            return !isCompleted;
          })
          .map(tag => {
            const dueDateInfo = formatDueDate(tag.due_date);
            const dueDateStatus = getDueDateStatus(tag.due_date);
            
            // Calculate completion status and task count
            const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
            const completedTasks = tagTasks.filter(task => ['completed', 'released'].includes(task.status));
            const isCompleted = tagTasks.length > 0 && completedTasks.length === tagTasks.length;
            const taskCount = tagTasks.length;
            
            return (
            <div
              key={tag.id}
              className={`tag-bar__tag-container ${selectedTagId === tag.id ? 'active' : ''} ${dueDateStatus} ${isCompleted ? 'completed' : ''}`}
            >
              <button
                className="tag-bar__filter"
                onClick={() => handleTagSelect(tag.id)}
              >
                <FeatureTag 
                  name={tag.name} 
                  color={tag.color}
                  size="small"
                />
                <span className="tag-bar__count">({taskCount})</span>
                {isCompleted && (
                  <span className="tag-bar__completed-indicator" title="All tasks completed">
                    ✓
                  </span>
                )}
                
                {/* Description, Artifacts, Links Indicators */}
                <div className="tag-bar__info-indicators">
                  {tag.description && (
                    <span className="tag-bar__info-indicator" title="Has description">
                      <FileText size={10} />
                    </span>
                  )}
                  {tag.artifacts_count > 0 && (
                    <span className="tag-bar__info-indicator" title={`${tag.artifacts_count} artifact${tag.artifacts_count !== 1 ? 's' : ''}`}>
                      <File size={10} />
                      <span className="tag-bar__info-count">{tag.artifacts_count}</span>
                    </span>
                  )}
                  {tag.links_count > 0 && (
                    <span className="tag-bar__info-indicator" title={`${tag.links_count} link${tag.links_count !== 1 ? 's' : ''}`}>
                      <LinkIcon size={10} />
                      <span className="tag-bar__info-count">{tag.links_count}</span>
                    </span>
                  )}
                </div>
                
                {/* Due Date Display */}
                {dueDateInfo && !isCompleted && (
                  <span className={`tag-bar__due-date tag-bar__due-date--${dueDateStatus}`}>
                    {dueDateStatus === 'overdue' && <AlertTriangle size={12} />}
                    {dueDateStatus === 'due-today' && <Clock size={12} />}
                    {dueDateStatus === 'urgent' && <Clock size={12} />}
                    {dueDateStatus === 'upcoming' && <Calendar size={12} />}
                    {dueDateStatus === 'normal' && <Calendar size={12} />}
                    <span>{dueDateInfo.text}</span>
                  </span>
                )}
              </button>
              
              {/* Details Button */}
              <button
                className="tag-bar__details-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDetailModal(tag);
                }}
                title="View detailed analysis"
              >
                <BarChart2 size={12} />
              </button>
              
              {/* Due Date Edit Button */}
              <button
                className="tag-bar__due-date-edit-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartDueDateEdit(tag);
                }}
                title={`${tag.due_date ? 'Edit' : 'Set'} due date`}
              >
                <Calendar size={12} />
              </button>
              
              <button
                className="tag-bar__delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTag(tag);
                }}
                title={`Delete "${tag.name}" tag`}
              >
                <X size={12} />
              </button>
              
              {/* Due Date Edit Form - rendered inside each tag container */}
              {editingDueDate === tag.id && (
                <div className="tag-bar__due-date-edit-form">
                  <div className="tag-bar__due-date-edit-content">
                    <label className="tag-bar__due-date-label">Set Due Date:</label>
                    <input
                      type="date"
                      value={newDueDate}
                      onChange={(e) => setNewDueDate(e.target.value)}
                      className="tag-bar__due-date-input"
                      min={new Date().toISOString().split('T')[0]}
                      autoFocus
                    />
                    <div className="tag-bar__due-date-actions">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => handleSaveDueDate(tag.id)}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="neutral-tertiary"
                        size="sm"
                        onClick={handleCancelDueDateEdit}
                      >
                        Cancel
                      </Button>
                      {newDueDate && (
                        <Button
                          type="button"
                          variant="neutral-tertiary"
                          size="sm"
                          onClick={() => {
                            setNewDueDate('');
                            handleSaveDueDate(tag.id);
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Create New Tag */}
        {showCreateForm ? (
          <form onSubmit={handleCreateTag} className="tag-bar__create-form">
            <TextField>
              <TextField.Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Tag name..."
                autoFocus
                maxLength={50}
                className="tag-bar__input"
              />
            </TextField>
            <div className="tag-bar__create-actions">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!newTagName.trim() || creating}
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
              <Button
                type="button"
                variant="neutral-tertiary"
                size="sm"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewTagName('');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <button
            className="tag-bar__add-button"
            onClick={() => setShowCreateForm(true)}
            title="Add new tag"
          >
            <Plus size={16} />
            <span>Add Tag</span>
          </button>
        )}
      </div>

      {/* Completed Tags - Second Line */}
      {tags.some(tag => {
        const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
        if (tagTasks.length === 0) return false;
        const completedTasks = tagTasks.filter(task => ['completed', 'released'].includes(task.status));
        return tagTasks.length > 0 && completedTasks.length === tagTasks.length;
      }) && (() => {
        const completedTags = tags.filter(tag => {
          const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
          if (tagTasks.length === 0) return false;
          const completedTasks = tagTasks.filter(task => ['completed', 'released'].includes(task.status));
          return tagTasks.length > 0 && completedTasks.length === tagTasks.length;
        });
        return (
        <div className={`tag-bar__completed-section ${!showCompletedSection ? 'tag-bar__completed-section--collapsed' : ''}`}>
          <button
            type="button"
            className="tag-bar__completed-toggle"
            onClick={() => setShowCompletedSection(!showCompletedSection)}
            title={showCompletedSection ? 'Hide completed tags' : 'Show completed tags'}
          >
            <CheckCircle size={14} />
            <span>Completed ({completedTags.length})</span>
            {showCompletedSection ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <div className="tag-bar__completed-tags">
            {tags
              .filter(tag => {
                // Hide tags with no tasks
                const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
                if (tagTasks.length === 0) return false;
                
                // Only show completed tags
                const completedTasks = tagTasks.filter(task => ['completed', 'released'].includes(task.status));
                return tagTasks.length > 0 && completedTasks.length === tagTasks.length;
              })
              .map(tag => {
                const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
                const taskCount = tagTasks.length;
                
                return (
                  <div
                    key={tag.id}
                    className={`tag-bar__tag-container tag-bar__tag-container--completed ${selectedTagId === tag.id ? 'active' : ''}`}
                  >
                    <button
                      className="tag-bar__filter"
                      onClick={() => handleTagSelect(tag.id)}
                    >
                      <FeatureTag 
                        name={tag.name} 
                        color={tag.color}
                        size="small"
                      />
                      <span className="tag-bar__count">({taskCount})</span>
                      <span className="tag-bar__completed-indicator" title="All tasks completed">
                        ✓
                      </span>
                      
                      {/* Description, Artifacts, Links Indicators */}
                      <div className="tag-bar__info-indicators">
                        {tag.description && (
                          <span className="tag-bar__info-indicator" title="Has description">
                            <FileText size={10} />
                          </span>
                        )}
                        {tag.artifacts_count > 0 && (
                          <span className="tag-bar__info-indicator" title={`${tag.artifacts_count} artifact${tag.artifacts_count !== 1 ? 's' : ''}`}>
                            <File size={10} />
                            <span className="tag-bar__info-count">{tag.artifacts_count}</span>
                          </span>
                        )}
                        {tag.links_count > 0 && (
                          <span className="tag-bar__info-indicator" title={`${tag.links_count} link${tag.links_count !== 1 ? 's' : ''}`}>
                            <LinkIcon size={10} />
                            <span className="tag-bar__info-count">{tag.links_count}</span>
                          </span>
                        )}
                      </div>
                    </button>
                    
                    {/* Details Button */}
                    <button
                      className="tag-bar__details-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetailModal(tag);
                      }}
                      title="View detailed analysis"
                    >
                      <BarChart2 size={12} />
                    </button>
                    
                    <button
                      className="tag-bar__delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTag(tag);
                      }}
                      title={`Delete "${tag.name}" tag`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
        );
      })()}

      {/* Quick Create Suggestions */}
      {/* {!showCreateForm && (
        <div className="tag-bar__suggestions">
          <span className="tag-bar__suggestions-label">Quick create:</span>
          {['Bug Fix', 'Feature', 'UI', 'Backend', 'Testing'].map(suggestion => (
            <button
              key={suggestion}
              className="tag-bar__suggestion"
              onClick={() => handleQuickCreate(suggestion)}
              disabled={tags.some(tag => tag.name.toLowerCase() === suggestion.toLowerCase())}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )} */}

      {/* Feature Tag Detail Modal */}
      <FeatureTagDetailModal
        tag={selectedTagForDetails}
        isOpen={showDetailModal}
        onClose={handleCloseDetailModal}
        tasks={tasks}
        users={users}
        onTagUpdate={onTagUpdate}
      />
    </div>
  );
};

export default TagBar;
